import { getTrfPdfConfigFields } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { useLaunchMode } from '@/features/auth/launchContext'
import type { CanonicalOrderPayload } from '@/ordering/canonicalOrder'
import { OrderFormInfoData, RequisitionFormData } from '@/types/RequisitionFormTypes'
import { warnClientSideTrfRender } from '@/utils/clientTrfRenderWarning'
import { fetchDraftTrfPdfBytes } from '@/utils/draftTrfPdf'
import { fetchOrderTrfPdfBytes } from '@/utils/orderTrfPdf'
import { fetchRegeneratedOrderTrfPdfBytes } from '@/utils/regeneratedOrderTrfPdf'
import { clientTrfPdfFilename } from '@/utils/trfPdfFilename'
import { renderTestRequisitionPDF, renderTestRequisitionPDFFromCanonical } from '@/utils/PDFRenderer'
import { downloadPdfBytes, openBlankPdfWindow, renderPdfBytesInWindow } from '@/utils/pdfBytesWindow'
import { logger } from '@/sentry.config'
import * as Sentry from '@sentry/react'
import React, { useEffect, useRef, useState } from 'react'
import PDFViewer from '../PDFViewer'

interface RequisitionFormViewProps {
  /** Header data for the client-side render path; not needed when a stored TRF is fetched. */
  orderData?: OrderFormInfoData | null
  /** Canonical order payload — preferred source for new session-based orders. */
  canonical?: CanonicalOrderPayload | null
  /** Legacy TRF payload — still accepted for IMDB / in-progress order views. */
  formData?: RequisitionFormData | null
  /**
   * Whether this TRF is rendered for an EHR (Epic SMART) launch. When provided, this is
   * authoritative; otherwise it falls back to the current browser launch mode. Pass it from
   * callers that already know the flow (e.g. OrderReadyToSend's `isEpicFlow`) so the EHR-vs-web
   * TRF differences match the flow the order was actually placed in.
   */
  isEhrOrder?: boolean
  showHeader?: boolean
  viewerClassName?: string
  className?: string
  /**
   * When true, defers PDF generation and shows the loading state instead. Callers pass this while
   * upstream data that feeds the TRF (e.g. the current user's `/auth/me/profile` phone/roleTitle)
   * is still resolving, so the PDF renders once with final data rather than rendering an initial
   * pass and re-rendering when that data arrives.
   */
  deferPdfRender?: boolean
  /**
   * Ordering session id. When provided, the TRF is generated **server-side** by provider-api
   * (GEN-4159): the component POSTs to the DRAFT-PDF endpoint, receives a short-lived pre-signed
   * S3 URL, fetches the bytes, and renders them via the existing `PDFViewer` — no client-side
   * `pdf-lib` generation. When absent, the legacy client-side render path is used.
   *
   * The backend renders from the persisted session `state.canonicalOrder`, so the caller must
   * PATCH the current (merged) canonical into the session before this component fetches. Bump
   * `draftRefreshToken` once that PATCH has landed to (re)trigger the fetch.
   */
  sessionId?: string | null
  /**
   * Changes whenever the persisted session canonical changes (e.g. after the caller PATCHes the
   * merged providers / authorizing clinician / encounter). Used only in the server-side
   * (`sessionId`) path: the fetch waits for a truthy token so it never reads a stale session,
   * and re-fetches when the token changes.
   */
  draftRefreshToken?: string | number | null
  /**
   * Placed order id. When provided (and there is no `sessionId`), the TRF is fetched from the
   * artifact provider-api stored at submit (GEN-4161): the component reads the short-lived
   * pre-signed S3 URL off the order-details response, fetches the bytes, and renders them via
   * the existing `PDFViewer` — so a returning user sees the document that was actually
   * submitted rather than a client-side re-render of it.
   *
   * Orders placed before server-side generation shipped may have no artifact; those fall back
   * to the legacy client-side render path automatically.
   */
  orderId?: string | null
  /** Audit origin for the order-details read that resolves the pre-signed URL. */
  trfFetchCaller?: string
  /**
   * When true (and an `orderId` is present without a `sessionId`), the TRF is re-rendered
   * server-side from the supplied `canonical` rather than fetched from the stored submit-time
   * artifact. Used by the in-progress "View Order Form" so unsent edits are reflected: the
   * component POSTs the current canonical to the order regenerate-pdf endpoint, receives a
   * short-lived pre-signed S3 URL, fetches the bytes, and renders them via `PDFViewer`.
   *
   * Requires `canonical` to be present; when it is absent this flag is ignored and the stored
   * artifact path is used.
   */
  regenerateFromCanonical?: boolean
  /**
   * Changes whenever the caller wants the regenerated TRF re-fetched (e.g. each time the viewer
   * is (re)opened) so the newest canonical is rendered. Used only in the regenerate path.
   */
  regenerateRefreshToken?: string | number | null
  /**
   * When true, renders an integrated control row showing the "Requisition Form" heading, the
   * page count, a Download action, and a View full PDF action. The PDFViewer's zoom/print
   * toolbar is suppressed so there is never more than one set of PDF controls on screen.
   *
   * Intended for the Ready-to-Send page, where the outer page does not provide a separate
   * section heading or PDF actions and the design shows them inline with the section.
   * Use alongside ``showHeader={false}``.
   *
   * Defaults to ``false`` so existing callers (modal embed, order form viewer) are unaffected.
   */
  showControlRow?: boolean
}

const RequisitionFormView: React.FC<RequisitionFormViewProps> = ({
  orderData,
  canonical,
  formData,
  isEhrOrder: isEhrOrderProp,
  showHeader = true,
  viewerClassName,
  className = '',
  deferPdfRender = false,
  sessionId,
  draftRefreshToken,
  orderId,
  trfFetchCaller = 'spa',
  regenerateFromCanonical = false,
  regenerateRefreshToken,
  showControlRow = false,
}) => {
  const { config } = useUiConfig()
  const { mode: launchMode } = useLaunchMode()
  const isEhrOrder = isEhrOrderProp ?? launchMode === 'epic_smart'
  const [requisitionPdfBytes, setRequisitionPdfBytes] = useState<Uint8Array | null>(null)
  const [serverPdfFilename, setServerPdfFilename] = useState<string | null>(null)
  const pdfFilename = serverPdfFilename ?? clientTrfPdfFilename(orderId)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [totalPages, setTotalPages] = useState(0)
  const [controlRowNotice, setControlRowNotice] = useState<string | null>(null)
  const containerClassName = showHeader ? 'rounded-lg border border-gray-200 bg-white shadow-sm' : 'w-full'
  const bodyHeightClass = viewerClassName || 'h-[calc(100vh-200px)]'

  const handleDownload = () => {
    if (!requisitionPdfBytes) return
    downloadPdfBytes(window, requisitionPdfBytes, pdfFilename)
  }

  const handleViewFullPdf = () => {
    if (!requisitionPdfBytes) return
    setControlRowNotice(null)
    const spawned = openBlankPdfWindow()
    if (!spawned) {
      Sentry.captureException(new Error('Popup blocked. Please allow popups for this site.'), {
        tags: { operation: 'ui.action.requisition_view_full_pdf' },
      })
      setControlRowNotice('Popups are blocked for this site. Allow them to open the requisition in a new tab.')
      return
    }
    renderPdfBytesInWindow(spawned, requisitionPdfBytes, { filename: pdfFilename }).catch((err: unknown) => {
      logger.error('PDF rendering in new window failed', {
        error: err instanceof Error ? err.message : String(err),
      })
      Sentry.captureException(err, { tags: { operation: 'ui.action.requisition_view_full_pdf' } })
      setControlRowNotice('The PDF could not be rendered in the new tab.')
    })
  }

  const hasCanonicalData = Boolean(canonical?.order?.selectedOption)
  const hasLegacyData = Boolean(formData?.order?.selectedOption)
  // Server-side generation (GEN-4159) is used whenever an ordering session id is available.
  const useServerDraft = Boolean(sessionId)
  /**
   * Regenerate path: an in-progress placed order with unsent edits. We re-render the TRF from the
   * current `canonical` instead of the stored submit-time artifact, so the viewer reflects edits
   * that have not yet been sent. Requires an `orderId` (no session) and a canonical to render from.
   */
  const useRegenerateOrderTrf = !useServerDraft && Boolean(orderId) && regenerateFromCanonical && Boolean(canonical)
  /**
   * Set when the backend reports that this order has no stored TRF, which hands the render
   * back to the legacy client-side path. Distinct from `pdfError`: a missing artifact is an
   * expected state for orders that predate server-side generation, whereas a failed fetch of
   * an artifact that *does* exist is a fault the user should see.
   */
  const [serverTrfMissing, setServerTrfMissing] = useState(false)
  const useServerOrderTrf = !useServerDraft && !useRegenerateOrderTrf && Boolean(orderId) && !serverTrfMissing
  /**
   * Keyed on reason + order so the Sentry issue count tracks documents the browser had to
   * build rather than React re-renders (the client-side effect re-runs on prop identity).
   */
  const reportedClientRenderRef = useRef<string | null>(null)

  // Re-test the server artifact when the viewer switches orders.
  useEffect(() => {
    setServerTrfMissing(false)
  }, [orderId])

  // ── Regenerate path: re-render the placed order's TRF from the current canonical ──
  useEffect(() => {
    if (!useRegenerateOrderTrf) return

    // Wait for upstream data to settle (mirrors the other paths).
    if (deferPdfRender) {
      setIsGeneratingPDF(true)
      return
    }

    const ac = new AbortController()
    const loadRegeneratedTrf = async () => {
      setPdfError(null)
      setRequisitionPdfBytes(null)
      setIsGeneratingPDF(true)
      try {
        const pdf = await fetchRegeneratedOrderTrfPdfBytes(
          orderId!,
          canonical!,
          { isEhrOrder },
          trfFetchCaller,
          ac.signal
        )
        if (!ac.signal.aborted) {
          setServerPdfFilename(pdf.filename)
          setRequisitionPdfBytes(pdf.bytes)
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        Sentry.captureException(error, { tags: { operation: 'order.details.trf_pdf.regenerate' } })
        setPdfError(error instanceof Error ? error.message : 'Failed to load PDF')
      } finally {
        if (!ac.signal.aborted) {
          setIsGeneratingPDF(false)
        }
      }
    }

    loadRegeneratedTrf()
    return () => ac.abort()
    // `canonical` is intentionally omitted: the caller bumps `regenerateRefreshToken` when the
    // canonical it wants rendered has changed, so we re-fetch on the token (not on every prop
    // identity change of the canonical object).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useRegenerateOrderTrf, orderId, regenerateRefreshToken, isEhrOrder, trfFetchCaller, deferPdfRender])

  // ── Placed-order path: fetch the stored TRF via the order-details pre-signed URL ──
  useEffect(() => {
    if (!useServerOrderTrf) return

    // Wait for upstream data to settle (mirrors the other paths).
    if (deferPdfRender) {
      setIsGeneratingPDF(true)
      return
    }

    const ac = new AbortController()
    let handedOffToClientRender = false
    const loadStoredTrf = async () => {
      setPdfError(null)
      setRequisitionPdfBytes(null)
      setIsGeneratingPDF(true)
      try {
        const pdf = await fetchOrderTrfPdfBytes(orderId!, trfFetchCaller, ac.signal)
        if (ac.signal.aborted) return
        if (pdf) {
          setServerPdfFilename(pdf.filename)
          setRequisitionPdfBytes(pdf.bytes)
        } else {
          // Keep the spinner up: the client-side effect takes over on the next render.
          handedOffToClientRender = true
          setServerTrfMissing(true)
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        Sentry.captureException(error, { tags: { operation: 'order.details.trf_pdf.fetch' } })
        setPdfError(error instanceof Error ? error.message : 'Failed to load PDF')
      } finally {
        if (!ac.signal.aborted && !handedOffToClientRender) {
          setIsGeneratingPDF(false)
        }
      }
    }

    loadStoredTrf()
    return () => ac.abort()
  }, [useServerOrderTrf, orderId, trfFetchCaller, deferPdfRender])

  // ── Server-side path: fetch the DRAFT TRF PDF from provider-api (pre-signed S3 URL) ──
  useEffect(() => {
    if (!useServerDraft) return

    // Wait for upstream data to settle (mirrors the client-side path).
    if (deferPdfRender) {
      setIsGeneratingPDF(true)
      return
    }
    // Wait until the caller signals the persisted session canonical is up to date, so the
    // backend never renders from a stale session (authorizing clinician / encounter / phone).
    if (!draftRefreshToken) {
      setIsGeneratingPDF(true)
      return
    }

    const ac = new AbortController()
    const loadServerDraft = async () => {
      setPdfError(null)
      setRequisitionPdfBytes(null)
      setIsGeneratingPDF(true)
      try {
        const pdf = await fetchDraftTrfPdfBytes(sessionId!, { isEhrOrder }, ac.signal)
        if (!ac.signal.aborted) {
          setServerPdfFilename(pdf.filename)
          setRequisitionPdfBytes(pdf.bytes)
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        Sentry.captureException(error, { tags: { operation: 'order.review.draft_pdf.fetch' } })
        setPdfError(error instanceof Error ? error.message : 'Failed to load PDF')
      } finally {
        if (!ac.signal.aborted) {
          setIsGeneratingPDF(false)
        }
      }
    }

    loadServerDraft()
    return () => ac.abort()
  }, [useServerDraft, sessionId, draftRefreshToken, isEhrOrder, deferPdfRender])

  // ── Legacy client-side path: build the TRF in-browser with pdf-lib ──
  useEffect(() => {
    if (useServerDraft || useServerOrderTrf || useRegenerateOrderTrf) return

    const generateRequisitionPDF = async () => {
      // Wait for upstream data (e.g. the current user's profile) to resolve before rendering,
      // so the PDF is generated once with final data instead of an initial pass + re-render.
      if (deferPdfRender) {
        setIsGeneratingPDF(true)
        return
      }

      setPdfError(null)
      setRequisitionPdfBytes(null)

      if (!orderData) {
        setPdfError('Missing order data. Please ensure you navigated from a valid order flow.')
        setIsGeneratingPDF(false)
        return
      }
      if (!hasCanonicalData && !hasLegacyData) {
        setPdfError('Missing form data. Please ensure the form was completed.')
        setIsGeneratingPDF(false)
        return
      }

      try {
        setIsGeneratingPDF(true)
        setPdfError(null)

        const reason = serverTrfMissing ? 'server_artifact_missing' : 'no_session_or_order_id'
        const clientRenderKey = `${reason}:${orderId ?? ''}`
        if (reportedClientRenderRef.current !== clientRenderKey) {
          reportedClientRenderRef.current = clientRenderKey
          warnClientSideTrfRender({ reason, renderer: hasCanonicalData ? 'canonical' : 'legacy_rfd', orderId })
        }

        const trfPdf = getTrfPdfConfigFields(config)
        const pdfBytes = hasCanonicalData
          ? await renderTestRequisitionPDFFromCanonical(canonical!, orderData, trfPdf, isEhrOrder)
          : await renderTestRequisitionPDF(formData!, orderData, trfPdf, isEhrOrder)

        setServerPdfFilename(null)
        setRequisitionPdfBytes(pdfBytes)
      } catch (error) {
        console.error('Error generating requisition PDF:', error)
        setPdfError(error instanceof Error ? error.message : 'Failed to generate PDF')
      } finally {
        setIsGeneratingPDF(false)
      }
    }

    generateRequisitionPDF()
  }, [
    useServerDraft,
    useServerOrderTrf,
    useRegenerateOrderTrf,
    serverTrfMissing,
    orderId,
    orderData,
    canonical,
    formData,
    hasCanonicalData,
    hasLegacyData,
    config,
    isEhrOrder,
    deferPdfRender,
  ])

  return (
    <div className={`${containerClassName} ${className}`.trim()}>
      {showHeader && (
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="page-navigation-title">Requisition Form</h3>
        </div>
      )}
      {showControlRow && !pdfError && (
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-6 py-3">
          <h3 className="min-w-0 flex-1 text-2xl font-semibold leading-8 tracking-tight text-text-heading">
            Requisition Form
          </h3>
          <span className="whitespace-nowrap text-xs leading-5 text-text-body">
            {totalPages > 0 && requisitionPdfBytes ? `${totalPages} page${totalPages !== 1 ? 's' : ''}` : '—'}
          </span>
          <button
            onClick={handleDownload}
            disabled={!requisitionPdfBytes}
            className="inline-flex shrink-0 items-center justify-center rounded-full border border-border-base bg-white px-4 py-2 text-sm font-medium leading-5 text-text-body shadow-xs transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            Download
          </button>
          <button
            onClick={handleViewFullPdf}
            disabled={!requisitionPdfBytes}
            className="inline-flex shrink-0 items-center justify-center rounded-full bg-brand-600 px-4 py-2 text-sm font-medium leading-5 text-white shadow-xs transition-colors hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            View full PDF
          </button>
        </div>
      )}
      {showControlRow && controlRowNotice && (
        <div
          role="alert"
          className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800"
        >
          <span>{controlRowNotice}</span>
          <button onClick={() => setControlRowNotice(null)} className="font-medium underline" title="Dismiss">
            Dismiss
          </button>
        </div>
      )}
      <div className={`${bodyHeightClass} overflow-auto`}>
        {isGeneratingPDF ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
              <p className="body-text mt-2 text-gray-600">Generating PDF...</p>
            </div>
          </div>
        ) : pdfError ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center">
              <p className="mb-4 text-red-600">{pdfError}</p>
              <p className="text-sm text-gray-500">Unable to display requisition form</p>
            </div>
          </div>
        ) : requisitionPdfBytes ? (
          <PDFViewer
            pdfBytes={requisitionPdfBytes}
            filename={pdfFilename}
            className="h-full"
            showToolbar={!showControlRow}
            onTotalPagesChange={showControlRow ? setTotalPages : undefined}
          />
        ) : null}
      </div>
    </div>
  )
}

export default RequisitionFormView
